import { Controller, useFormContext } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { Input } from '@ds';
import { BaseSupplementalLiftSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { WarningTypeEnum } from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { useBannedTailNumbersAllQuery } from '@/queries/banned-tail-numbers';

type NonTmdxAviaInputsProps = {
  withBannedTailNumberCheck?: boolean;
  withRoutePrice?: boolean;
};

export const NonTmdxAviaInputs = ({
  withBannedTailNumberCheck = false,
  withRoutePrice = false,
}: NonTmdxAviaInputsProps) => {
  const { isMainDataEdit, isOptionalDataEdit, addWarningMessage, removeWarningMessage } = useLegFormContext();
  const { control, setValue } = useFormContext<BaseSupplementalLiftSchemaValues>();
  const { data: bannedTailNumbers } = useBannedTailNumbersAllQuery(withBannedTailNumberCheck);

  const onTailNumberChange = (value: string) => {
    setValue('tailNumber', value);

    const hasBannedItem =
      withBannedTailNumberCheck && value && bannedTailNumbers?.items.find((t) => t.tailNumber === value);

    if (hasBannedItem) {
      addWarningMessage(WarningTypeEnum.BannedTailNumber);
    } else {
      removeWarningMessage(WarningTypeEnum.BannedTailNumber);
    }
  };

  return (
    <div className="flex flex-row gap-6">
      <Controller
        name="tailNumber"
        control={control}
        render={({ field: { onChange, ...field }, fieldState: { invalid } }) => (
          <div className="w-32">
            <Input
              size="md"
              aria-label="Tail Number"
              label="Tail Number"
              placeholder="Tail Number"
              isInvalid={invalid}
              isDisabled={!isOptionalDataEdit}
              onValueChange={onTailNumberChange}
              {...field}
            />
          </div>
        )}
      />
      {withRoutePrice && (
        <Controller
          name="priceInCents"
          control={control}
          render={({ field: { onChange, ref, ...field }, fieldState: { invalid } }) => (
            <div className="flex flex-col w-auto gap-1">
              <NumericFormat
                prefix="$"
                thousandSeparator
                valueIsNumericString
                decimalScale={2}
                customInput={Input}
                aria-label="Total route price"
                label="Total route price *"
                placeholder="Total route price"
                classNames={{
                  inputWrapper: 'bg-base-60! rounded-lg',
                }}
                isInvalid={invalid}
                isDisabled={!isMainDataEdit}
                onValueChange={(values) => {
                  onChange(values.value);
                }}
                {...field}
              />
              <span className="text-s-r text-base-20 p-0">Is set up for entire route (all legs)</span>
            </div>
          )}
        />
      )}
    </div>
  );
};
